import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { UserService } from '@core/services/user.service';
import { KbdComponent } from '@shared/components/kbd/kbd.component';
import { UiIconComponent } from '@shared/components/ui-icon/ui-icon.component';
import {
  GrupoResultados,
  MIN_CARACTERES_BUSQUEDA,
  PaginaBusqueda,
  paginaVacia,
  ResultadoBusqueda,
  TipoResultado,
} from '@shared/models/busqueda-global.model';
import { BusquedaGlobalService } from '@shared/services/busqueda-global.service';
import { BusquedaNavegacionService } from '@shared/services/busqueda-navegacion.service';
import {
  BusquedaRecientesService,
  OperacionReciente,
} from '@shared/services/busqueda-recientes.service';

/** Filas por familia en el desplegable. Menos que en una página: aquí se ojea. */
const TOP_POR_TIPO = 4;

/** Retardo antes de consultar: se teclea texto libre y cada pulsación costaría un viaje al SP. */
const DEBOUNCE_MS = 300;

/** Icono por familia (Material Symbols). */
const ICONO_TIPO: Record<TipoResultado, string> = {
  despacho: 'local_shipping',
  orden_compra: 'receipt_long',
  importacion: 'directions_boat',
  tarifa: 'request_quote',
};

/**
 * Elemento navegable con las flechas. El desplegable mezcla resultados,
 * operaciones recientes y textos buscados; la navegación por teclado los ve
 * como una sola lista plana.
 */
type ItemNav =
  | { kind: 'resultado'; resultado: ResultadoBusqueda }
  | { kind: 'operacion'; operacion: OperacionReciente }
  | { kind: 'consulta'; texto: string };

/**
 * Buscador global del navbar de proveedores.
 *
 * Busca despachos, órdenes de compra, importaciones y tarifas dentro del
 * alcance activo (empresa + proveedor) y abre el detalle elegido. Con el campo
 * corto o vacío muestra el historial (operaciones y textos recientes).
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, KbdComponent, UiIconComponent],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
})
export class GlobalSearchComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly userService = inject(UserService);
  private readonly service = inject(BusquedaGlobalService);
  private readonly navegacion = inject(BusquedaNavegacionService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);

  protected readonly recientes = inject(BusquedaRecientesService);

  protected readonly MIN_CARACTERES = MIN_CARACTERES_BUSQUEDA;
  protected readonly ICONO_TIPO = ICONO_TIPO;

  // ── Estado ────────────────────────────────────────────────────────────────

  protected readonly query = signal<string>('');
  protected readonly activeIndex = signal<number>(0);
  protected readonly cargando = signal<boolean>(false);
  protected readonly pagina = signal<PaginaBusqueda>(paginaVacia());
  protected readonly abierto = signal<boolean>(false);

  /**
   * Término para el que se pulsó Enter mientras cargaba. Al llegar la respuesta
   * de ESE término se abre su primer resultado; cambiar el término o cerrar el
   * panel lo cancela.
   */
  private readonly enterPendiente = signal<string | null>(null);

  /** Alcance activo; entra en el stream para que cambiar de empresa relance la consulta. */
  private readonly scope = computed(() => ({
    empresaId: this.userService.companyId(),
    proveedorId: this.userService.proveedorId(),
  }));

  /** Sin empresa o proveedor no se consulta: el panel lo dice en vez de «Sin resultados». */
  protected readonly sinAlcance = computed(() => {
    const { empresaId, proveedorId } = this.scope();
    return (
      empresaId === null ||
      !Number.isFinite(empresaId) ||
      proveedorId === null ||
      !Number.isFinite(proveedorId)
    );
  });

  /** Término efectivo: por debajo del mínimo no se consulta nada. */
  private readonly terminoEfectivo = computed(() => {
    const q = this.query().trim();
    return q.length >= MIN_CARACTERES_BUSQUEDA ? q : '';
  });

  /** Con el campo corto o vacío el panel muestra el historial, no resultados. */
  protected readonly mostrarHistorial = computed(
    () => this.terminoEfectivo().length === 0,
  );

  protected readonly grupos = computed<GrupoResultados[]>(
    () => this.pagina().grupos,
  );
  protected readonly error = computed(() => this.pagina().error ?? null);

  protected readonly sinResultados = computed(
    () =>
      !this.mostrarHistorial() &&
      !this.cargando() &&
      !this.error() &&
      this.grupos().length === 0,
  );

  /** Idioma activo como signal, para re-traducir las etiquetas de grupo. */
  private readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly flatItems = computed<ItemNav[]>(() => {
    if (this.mostrarHistorial()) {
      return [
        ...this.recientes
          .operaciones()
          .map((operacion) => ({ kind: 'operacion' as const, operacion })),
        ...this.recientes
          .consultas()
          .map((texto) => ({ kind: 'consulta' as const, texto })),
      ];
    }

    return this.grupos().flatMap((g) =>
      g.items.map((resultado) => ({ kind: 'resultado' as const, resultado })),
    );
  });

  /** Clave del elemento resaltado, para pintarlo desde la plantilla. */
  protected readonly activeKey = computed<string | null>(() => {
    const item = this.flatItems()[this.activeIndex()];
    return item ? this.keyDe(item) : null;
  });

  /** Filas fantasma mientras carga. */
  protected readonly skeletonRows = Array.from({ length: 4 }, (_, i) => i);

  constructor() {
    toObservable(
      computed(() => ({
        q: this.terminoEfectivo(),
        scope: this.scope(),
        lang: this.activeLang(),
        abierto: this.abierto(),
      })),
    )
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        // Con el panel cerrado no se consulta nada.
        filter(({ abierto }) => abierto),
        tap(({ q }) => this.cargando.set(q.length > 0)),
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, scope }) => {
          if (!q) return of({ q, pagina: paginaVacia() });
          return this.service
            .consultar({
              q,
              empresaId: scope.empresaId,
              proveedorId: scope.proveedorId,
              topPorTipo: TOP_POR_TIPO,
            })
            .pipe(map((pagina) => ({ q, pagina })));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ q, pagina }) => {
        this.pagina.set(pagina);
        this.cargando.set(false);
        this.atenderEnterPendiente(q);
      });

    // Cualquier cambio de contenido devuelve el resaltado al principio.
    effect(() => {
      this.flatItems();
      this.activeIndex.set(0);
    });

    // Atrás/adelante u otra navegación no deja el panel abierto sobre la página nueva.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.cerrar());
  }

  // ── Atajos globales ───────────────────────────────────────────────────────

  /** «/» enfoca el buscador, salvo que el foco ya esté en un campo editable. */
  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('input, textarea, select, [contenteditable]')
    ) {
      return;
    }

    const input =
      this.host.nativeElement.querySelector<HTMLInputElement>(
        '[data-lrp-search]',
      );
    if (!input) return;
    event.preventDefault();
    input.focus();
  }

  /** Esc cierra el panel aunque el foco esté en una fila y no en el campo. */
  @HostListener('keydown.escape')
  protected onEscape(): void {
    this.cerrar();
  }

  /** Cierra al hacer clic fuera del componente. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.abierto()) return;
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      this.cerrar();
    }
  }

  // ── Entrada del usuario ───────────────────────────────────────────────────

  protected onQueryChange(value: string): void {
    this.query.set(value);
    this.abierto.set(true);
    // Otro término: el Enter pendiente era para el anterior.
    if (this.enterPendiente() !== this.terminoEfectivo()) {
      this.enterPendiente.set(null);
    }
  }

  protected onFocus(): void {
    this.abierto.set(true);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const items = this.flatItems();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.abierto.set(true);
      this.activeIndex.update((i) => Math.min(items.length - 1, i + 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.update((i) => Math.max(0, i - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      // Mientras llega la respuesta, la lista pintada es la del término
      // anterior: abrirla sería abrir un resultado que ya no corresponde.
      // El Enter se guarda y se atiende cuando llegue la respuesta de este término.
      if (!this.mostrarHistorial() && this.cargando()) {
        this.enterPendiente.set(this.terminoEfectivo());
        return;
      }
      // Sin nada resaltado, Enter abre el primer resultado.
      const item = items[this.activeIndex()] ?? items[0];
      if (item) this.activar(item);
    }
  }

  protected activar(item: ItemNav): void {
    switch (item.kind) {
      case 'resultado':
        this.elegir(item.resultado, false);
        return;
      case 'operacion':
        this.elegir(this.aResultado(item.operacion), false);
        return;
      case 'consulta':
        this.query.set(item.texto);
        return;
    }
  }

  /** Clic normal; con Ctrl/Cmd abre en pestaña nueva. */
  protected onClickResultado(
    resultado: ResultadoBusqueda,
    event: MouseEvent,
  ): void {
    this.elegir(resultado, event.ctrlKey || event.metaKey);
  }

  protected onClickOperacion(
    operacion: OperacionReciente,
    event: MouseEvent,
  ): void {
    this.elegir(this.aResultado(operacion), event.ctrlKey || event.metaKey);
  }

  /** Botón central: pestaña nueva. */
  protected onAuxClickResultado(
    resultado: ResultadoBusqueda,
    event: MouseEvent,
  ): void {
    if (event.button !== 1) return;
    event.preventDefault();
    this.elegir(resultado, true);
  }

  protected onAuxClickOperacion(
    operacion: OperacionReciente,
    event: MouseEvent,
  ): void {
    if (event.button !== 1) return;
    event.preventDefault();
    this.elegir(this.aResultado(operacion), true);
  }

  protected limpiarHistorial(event: Event): void {
    event.stopPropagation();
    this.recientes.limpiar();
  }

  protected cerrar(): void {
    this.abierto.set(false);
    this.enterPendiente.set(null);
  }

  /**
   * Abre el primer resultado si había un Enter pendiente para el término `q`
   * que acaba de responder y el panel sigue abierto con ese mismo término.
   */
  private atenderEnterPendiente(q: string): void {
    const pendiente = this.enterPendiente();
    if (pendiente === null) return;
    this.enterPendiente.set(null);

    if (
      !q ||
      pendiente !== q ||
      q !== this.terminoEfectivo() ||
      !this.abierto()
    ) {
      return;
    }
    const primero = this.flatItems()[0];
    if (primero) this.activar(primero);
  }

  /**
   * Primero se anota en el historial (un fallo de localStorage no debe impedir
   * la navegación), luego se cierra el panel, se limpia el campo y se navega.
   */
  private elegir(fila: ResultadoBusqueda, nuevaPestana: boolean): void {
    try {
      this.recientes.registrarOperacion(fila);
      // Solo el término que llegó a buscarse (≥ 3 caracteres).
      this.recientes.registrarConsulta(this.terminoEfectivo());
    } catch {
      // Cuota llena o almacenamiento bloqueado: el historial es opcional.
    }
    this.cerrar();
    this.query.set('');
    this.navegacion.abrir(fila, nuevaPestana);
  }

  /**
   * Reconstruye el mínimo de `ResultadoBusqueda` que necesitan el historial y la
   * navegación: lo guardado en localStorage es un extracto, no la fila completa.
   */
  private aResultado(o: OperacionReciente): ResultadoBusqueda {
    return {
      tipo: o.tipo,
      id: o.id,
      numero: o.numero,
      consecutivo: null,
      empresaId: o.empresaId,
      empresa: o.empresa,
      cliente: null,
      abierto: o.abierto,
      fase: null,
      faseColor: null,
      fecha: null,
      relacion: null,
      motivo: 'numero',
      valorCoincidencia: null,
      coincidenciaIndirecta: false,
    };
  }

  // ── Ayudas de plantilla ───────────────────────────────────────────────────

  protected keyDe(item: ItemNav): string {
    switch (item.kind) {
      case 'resultado':
        return this.keyResultado(item.resultado);
      case 'operacion':
        return this.keyOperacion(item.operacion);
      case 'consulta':
        return this.keyConsulta(item.texto);
    }
  }

  protected keyResultado(r: ResultadoBusqueda): string {
    return `r:${r.tipo}:${r.id}`;
  }

  protected keyOperacion(o: OperacionReciente): string {
    return `o:${o.tipo}:${o.id}`;
  }

  protected keyConsulta(texto: string): string {
    return `c:${texto}`;
  }
}
